import { api } from '../../api';

// C2N POS API (c2n.api PosEndpoints.cs). Money in rupees; dates "yyyy-MM-dd".

export type PosRole = 'Owner' | 'Manager' | 'Cashier' | 'Captain';
export const ROLE_RANK: Record<PosRole, number> = { Owner: 4, Manager: 3, Cashier: 2, Captain: 1 };
export const can = (role: string | undefined, minimum: PosRole) => ROLE_RANK[(role ?? 'Captain') as PosRole] >= ROLE_RANK[minimum];

export interface PosSettings {
  venueName: string;
  venueCode: string;
  legalName?: string | null;
  gstin?: string | null;
  fssaiNumber?: string | null;
  billAddress?: string | null;
  billPhone?: string | null;
  invoicePrefix: string;
  serviceChargePercent: number;
  billFooter?: string | null;
}

export interface PosMe {
  restoId: number;
  venueName: string;
  name: string;
  role: PosRole;
  settings: PosSettings;
}

export interface PosStaff {
  id: number;
  name: string;
  role: PosRole;
  isActive: boolean;
  lastLoginAt?: string | null;
}

export interface MenuCategory {
  id: number;
  name: string;
  station: 'Kitchen' | 'Bar';
  sortOrder: number;
  isActive: boolean;
}

export interface MenuVariant {
  id: number;
  name: string;
  price: number;
}

export interface MenuItem {
  id: number;
  categoryId: number;
  name: string;
  code?: string | null;
  description?: string | null;
  foodType: 'Veg' | 'NonVeg' | 'Egg' | 'Drink';
  taxName: 'GST' | 'VAT' | 'None';
  taxRate: number;
  isAvailable: boolean;
  sortOrder: number;
  station: string;
  variants: MenuVariant[];
}

export interface PosMenu {
  categories: MenuCategory[];
  items: MenuItem[];
}

export interface PosOrderSummary {
  id: number;
  orderNo: number;
  orderType: OrderType;
  tableId?: number | null;
  tableName?: string | null;
  status: OrderStatus;
  covers: number;
  customerName?: string | null;
  total: number;
  itemCount: number;
  unsentCount: number;
  createdAt: string;
}

export interface PosArea { id: number; name: string; sortOrder: number }
export interface PosTable { id: number; areaId?: number | null; name: string; seats: number; sortOrder: number; isActive: boolean; openOrder?: PosOrderSummary | null }
export interface PosFloor { areas: PosArea[]; tables: PosTable[]; openOrders: PosOrderSummary[] }

export type OrderType = 'DineIn' | 'Takeaway' | 'Delivery';
export type OrderStatus = 'Open' | 'Billed' | 'Paid' | 'Cancelled';

export interface PosOrderItem {
  id: number;
  menuItemId: number;
  variantId?: number | null;
  name: string;
  variantName?: string | null;
  unitPrice: number;
  quantity: number;
  amount: number;
  taxName: string;
  taxRate: number;
  station: string;
  note?: string | null;
  kotId?: number | null;
  kotNo?: number | null;
  isVoid: boolean;
  voidReason?: string | null;
  voidedBy?: string | null;
  addedBy: string;
}

export interface PosTaxLine { label: string; rate: number; taxable: number; amount: number }
export interface PosPayment { id: number; method: string; amount: number; reference?: string | null; createdBy: string; createdAt: string }

export interface PosOrder {
  id: number;
  orderNo: number;
  businessDate: string;
  orderType: OrderType;
  tableId?: number | null;
  tableName?: string | null;
  covers: number;
  status: OrderStatus;
  customerName?: string | null;
  customerPhone?: string | null;
  note?: string | null;
  createdBy: string;
  createdAt: string;
  billNumber?: string | null;
  billedAt?: string | null;
  closedAt?: string | null;
  discountPercent?: number | null;
  discountFlat?: number | null;
  discountReason?: string | null;
  subtotal: number;
  discountAmount: number;
  serviceChargePercent: number;
  serviceCharge: number;
  taxes: PosTaxLine[];
  taxTotal: number;
  roundOff: number;
  total: number;
  paidTotal: number;
  balance: number;
  items: PosOrderItem[];
  payments: PosPayment[];
  cancelReason?: string | null;
}

export interface PosKot {
  id: number;
  kotNo: number;
  station: string;
  orderId: number;
  orderNo: number;
  orderType: OrderType;
  tableName?: string | null;
  covers: number;
  createdBy: string;
  createdAt: string;
  items: { name: string; variantName?: string | null; quantity: number; note?: string | null; isVoid: boolean }[];
}

export interface PosBill { order: PosOrder; venue: PosSettings }

export interface PosSalesReport {
  from: string;
  to: string;
  orders: number;
  covers: number;
  gross: number;
  discounts: number;
  serviceCharge: number;
  tax: number;
  roundOff: number;
  net: number;
  averageBill: number;
  payments: { method: string; count: number; amount: number }[];
  taxes: PosTaxLine[];
  items: { name: string; variantName?: string | null; quantity: number; amount: number }[];
  voids: { orderNo: number; item: string; quantity: number; amount: number; reason?: string | null; by?: string | null }[];
  cancellations: { orderNo: number; reason?: string | null; by?: string | null; value: number }[];
  byType: { orderType: string; orders: number; net: number }[];
}

export interface PosDayClose {
  businessDate: string;
  ordersCount: number;
  netSales: number;
  taxTotal: number;
  discounts: number;
  cashExpected: number;
  cashCounted: number;
  difference: number;
  note?: string | null;
  closedBy: string;
  closedAt: string;
}

export interface PosDayStatus { businessDate: string; openOrders: number; report: PosSalesReport; cashExpected: number; closed?: PosDayClose | null }

const get = async <T,>(url: string, params?: object) => (await api.get<T>(url, { params })).data;
const post = async <T,>(url: string, body?: unknown) => (await api.post<T>(url, body)).data;
const put = async <T,>(url: string, body?: unknown) => (await api.put<T>(url, body)).data;
const del = async (url: string) => { await api.delete(url); };

export const posApi = {
  staffLogin: (venueCode: string, pin: string) =>
    post<{ accessToken: string; expiresAtUtc: string; name: string; role: PosRole; venueName: string }>('/api/pos/staff-login', { venueCode, pin }),
  me: () => get<PosMe>('/api/pos/me'),
  saveSettings: (s: Omit<PosSettings, 'venueName' | 'venueCode'>) => put<PosSettings>('/api/pos/settings', s),
  staff: () => get<PosStaff[]>('/api/pos/staff'),
  saveStaff: (id: number | null, s: { name: string; role: string; pin?: string; isActive: boolean }) =>
    id ? put<PosStaff>(`/api/pos/staff/${id}`, s) : post<PosStaff>('/api/pos/staff', s),

  menu: () => get<PosMenu>('/api/pos/menu'),
  saveCategory: (id: number | null, c: { name: string; station: string; sortOrder: number; isActive: boolean }) =>
    id ? put<MenuCategory>(`/api/pos/menu/categories/${id}`, c) : post<MenuCategory>('/api/pos/menu/categories', c),
  deleteCategory: (id: number) => del(`/api/pos/menu/categories/${id}`),
  saveItem: (id: number | null, i: Omit<MenuItem, 'id' | 'station' | 'variants'> & { variants: { id?: number; name: string; price: number }[] }) =>
    id ? put<MenuItem>(`/api/pos/menu/items/${id}`, i) : post<MenuItem>('/api/pos/menu/items', i),
  deleteItem: (id: number) => del(`/api/pos/menu/items/${id}`),
  setAvailability: (id: number, isAvailable: boolean) => put<MenuItem>(`/api/pos/menu/items/${id}/availability`, { isAvailable }),

  floor: () => get<PosFloor>('/api/pos/floor'),
  saveArea: (id: number | null, a: { name: string; sortOrder: number }) => (id ? put<PosArea>(`/api/pos/areas/${id}`, a) : post<PosArea>('/api/pos/areas', a)),
  deleteArea: (id: number) => del(`/api/pos/areas/${id}`),
  saveTable: (id: number | null, t: { areaId?: number | null; name: string; seats: number; sortOrder: number; isActive: boolean }) =>
    id ? put<PosTable>(`/api/pos/tables/${id}`, t) : post<PosTable>('/api/pos/tables', t),
  deleteTable: (id: number) => del(`/api/pos/tables/${id}`),

  orders: (date?: string) => get<PosOrderSummary[]>('/api/pos/orders', date ? { date } : undefined),
  createOrder: (o: { orderType: OrderType; tableId?: number | null; covers: number; customerName?: string; customerPhone?: string }) => post<PosOrder>('/api/pos/orders', o),
  order: (id: number) => get<PosOrder>(`/api/pos/orders/${id}`),
  updateOrder: (id: number, o: { tableId?: number | null; covers: number; customerName?: string | null; customerPhone?: string | null; note?: string | null }) =>
    put<PosOrder>(`/api/pos/orders/${id}`, o),
  addItem: (id: number, menuItemId: number, variantId: number | undefined, quantity = 1, note?: string) =>
    post<PosOrder>(`/api/pos/orders/${id}/items`, { menuItemId, variantId, quantity, note }),
  updateItem: (id: number, itemId: number, quantity: number, note?: string | null) => put<PosOrder>(`/api/pos/orders/${id}/items/${itemId}`, { quantity, note }),
  removeItem: async (id: number, itemId: number) => (await api.delete<PosOrder>(`/api/pos/orders/${id}/items/${itemId}`)).data,
  voidItem: (id: number, itemId: number, reason: string) => post<PosOrder>(`/api/pos/orders/${id}/items/${itemId}/void`, { reason }),
  sendKot: (id: number) => post<PosKot[]>(`/api/pos/orders/${id}/kot`),
  kot: (id: number) => get<PosKot>(`/api/pos/kots/${id}`),
  discount: (id: number, d: { percent?: number | null; amount?: number | null; reason?: string }) => post<PosOrder>(`/api/pos/orders/${id}/discount`, d),
  bill: (id: number) => post<PosOrder>(`/api/pos/orders/${id}/bill`),
  billPrint: (id: number) => get<PosBill>(`/api/pos/orders/${id}/bill`),
  pay: (id: number, method: string, amount: number, reference?: string) => post<PosOrder>(`/api/pos/orders/${id}/payments`, { method, amount, reference }),
  cancel: (id: number, reason: string) => post<PosOrder>(`/api/pos/orders/${id}/cancel`, { reason }),

  sales: (from: string, to: string) => get<PosSalesReport>('/api/pos/reports/sales', { from, to }),
  day: (date?: string) => get<PosDayStatus>('/api/pos/day', date ? { date } : undefined),
  closeDay: (businessDate: string, cashCounted: number, note?: string) => post<PosDayClose>('/api/pos/day/close', { businessDate, cashCounted, note })
};

export const rupees = (n: number) => `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const errorText = (err: unknown, fallback = 'Something went wrong. Please try again.') =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

export const ORDER_TYPE_LABEL: Record<OrderType, string> = { DineIn: 'Dine-in', Takeaway: 'Takeaway', Delivery: 'Delivery' };

// The business day (5 AM to 5 AM, India time) as "yyyy-MM-dd", like the API's PosClock.
export const businessToday = () => {
  const ist = new Date(Date.now() + (5.5 * 60 - 5 * 60) * 60000);
  return ist.toISOString().slice(0, 10);
};
